export type PricingPlan = {
  id: string;
  code: string;
  name: string;
  description: string;
  monthlyCredits: number;
  billingInterval: string;
  currency: string;
  amount: number;
  taxLabel: string;
  highlighted: boolean;
  sortOrder: number;
};

function record(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) throw new Error('Invalid pricing response');
  return value as Record<string, unknown>;
}
function text(value: unknown): string {
  if (typeof value !== 'string' || !value.trim()) throw new Error('Invalid pricing text');
  return value;
}
function number(value: unknown): number {
  if ((typeof value !== 'string' && typeof value !== 'number') || String(value).trim() === '') throw new Error('Invalid pricing number');
  const result = Number(value);
  if (!Number.isFinite(result) || result < 0) throw new Error('Invalid pricing number');
  return result;
}

/** The public API sends credits and minor-unit amounts as decimal strings. */
export function parsePricing(value: unknown): PricingPlan[] {
  const data = record(value);
  const currency = text(data.checkoutCurrency);
  if (!['INR', 'USD'].includes(currency)) throw new Error('Unsupported checkout currency');
  if (!Array.isArray(data.subscriptionPlans) || !data.subscriptionPlans.length) throw new Error('No subscription plans available');
  const plans = data.subscriptionPlans.map((entry): PricingPlan => {
    const plan = record(entry);
    const price = record(record(plan.prices)[currency]);
    const tax = price.tax == null ? null : record(price.tax);
    return {
      id: text(plan.id), code: text(plan.code), name: text(plan.name),
      description: typeof plan.description === 'string' ? plan.description : '',
      monthlyCredits: number(plan.monthlyCredits), billingInterval: text(plan.billingInterval),
      currency, amount: number(price.amountMinor) / 100,
      taxLabel: tax ? `${tax.inclusive ? 'Includes' : '+'} ${text(tax.label)} (${number(tax.rateBps) / 100}%)` : '',
      highlighted: plan.highlighted === true, sortOrder: number(plan.sortOrder),
    };
  });
  if (new Set(plans.map(plan => plan.id)).size !== plans.length) throw new Error('Duplicate pricing plans');
  return plans.sort((a, b) => a.sortOrder - b.sortOrder);
}

export function formatPlanPrice(plan: PricingPlan): string {
  return new Intl.NumberFormat(plan.currency === 'INR' ? 'en-IN' : 'en-US', {
    style: 'currency', currency: plan.currency, minimumFractionDigits: 2,
  }).format(plan.amount);
}
